import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { register } from '../api';
import '../home.css';
import { LangSwitcher } from '../i18n';

const s = {
    root:   { minHeight: '100vh', background: '#050810', display: 'flex', flexDirection: 'column', fontFamily: '"Segoe UI", system-ui, sans-serif' },
    nav:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 48px', borderBottom: '1px solid rgba(29,78,216,0.15)' },
    logo:   { display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: '#f1f5f9', cursor: 'pointer' },
    logoI:  { width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg,#1d4ed8,#06b6d4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 },
    logoT:  { fontWeight: 800, fontSize: 18, color: '#f1f5f9' },
    center: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' },
    box:    { width: '100%', maxWidth: 420, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(29,78,216,0.2)', borderRadius: 20, padding: '40px 36px' },
    h1:     { fontSize: 26, fontWeight: 800, color: '#f1f5f9', marginBottom: 6, letterSpacing: '-0.5px' },
    sub:    { fontSize: 14, color: '#64748b', marginBottom: 32 },
    lbl:    { display: 'block', fontSize: 13, fontWeight: 600, color: '#94a3b8', marginBottom: 6, marginTop: 20 },
    inp:    { width: '100%', padding: '12px 14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, fontSize: 14, color: '#f1f5f9', outline: 'none', boxSizing: 'border-box', transition: 'border-color .2s' },
    inpFoc: { borderColor: 'rgba(29,78,216,0.6)' },
    err:    { marginTop: 16, padding: '12px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#fca5a5', fontSize: 13 },
    ok:     { marginTop: 16, padding: '12px 14px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8, color: '#86efac', fontSize: 13 },
    btn:    { width: '100%', marginTop: 28, padding: '13px 0', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer' },
    divider:{ display: 'flex', alignItems: 'center', gap: 12, margin: '24px 0', color: '#334155', fontSize: 12 },
    divLine:{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' },
    login:  { textAlign: 'center', fontSize: 14, color: '#64748b' },
    loginL: { color: '#60a5fa', cursor: 'pointer', fontWeight: 600 },
    terms:  { marginTop: 20, textAlign: 'center', fontSize: 12, color: '#334155' },
    termsL: { color: '#4b5563', cursor: 'pointer', textDecoration: 'underline' },
};

export default function Register() {
    const navigate = useNavigate();
    const [form, setForm]   = useState({ username: '', email: '', password: '' });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [focus, setFocus] = useState('');

    useEffect(() => {
        if (localStorage.getItem('token')) navigate('/dashboard', { replace: true });
    }, [navigate]);

    const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        if (!form.username.trim()) { setError('Username is required'); return; }
        if (!form.email.trim())    { setError('Email is required'); return; }
        if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }

        setLoading(true);
        try {
            const res = await register(form.username.trim(), form.email.trim().toLowerCase(), form.password);
            const { token, client } = res.data;
            localStorage.setItem('token', token);
            localStorage.setItem('client', JSON.stringify(client));
            navigate('/dashboard', { replace: true });
        } catch (err) {
            setError(err.response?.data?.error || 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const inputStyle = (field) => ({
        ...s.inp,
        ...(focus === field ? s.inpFoc : {}),
    });

    return (
        <div style={s.root}>
            <nav style={s.nav}>
                <div style={s.logo} onClick={() => navigate('/')}>
                    <img src="/logo.svg" alt="OrbitGrowth" style={{ height: 36, width: 'auto', borderRadius: 8, cursor: 'pointer' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <LangSwitcher />
                    <button onClick={() => navigate('/login')} style={{
                        padding: '9px 20px', background: 'none', border: '1px solid rgba(29,78,216,0.4)',
                        borderRadius: 10, color: '#60a5fa', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                    }}>Login</button>
                </div>
            </nav>

            <div style={s.center}>
                <div style={s.box}>
                    <h1 style={s.h1}>Create your account</h1>
                    <p style={s.sub}>Start growing your Instagram today. Free to join.</p>

                    <form onSubmit={submit}>
                        {/* Honeypot — nascosto agli utenti reali, i bot lo compilano */}
                        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }} aria-hidden="true">
                            <label>Website</label>
                            <input type="text" name="_trap" tabIndex="-1" autoComplete="off" />
                        </div>

                        <label style={s.lbl}>Username</label>
                        <input
                            style={inputStyle('username')}
                            type="text"
                            autoComplete="username"
                            value={form.username}
                            onChange={e => update('username', e.target.value)}
                            onFocus={() => setFocus('username')}
                            onBlur={() => setFocus('')}
                            placeholder="yourhandle"
                            required
                        />

                        <label style={s.lbl}>Email</label>
                        <input
                            style={inputStyle('email')}
                            type="email"
                            autoComplete="email"
                            value={form.email}
                            onChange={e => update('email', e.target.value)}
                            onFocus={() => setFocus('email')}
                            onBlur={() => setFocus('')}
                            placeholder="you@example.com"
                            required
                        />

                        <label style={s.lbl}>Password</label>
                        <input
                            style={inputStyle('password')}
                            type="password"
                            autoComplete="new-password"
                            value={form.password}
                            onChange={e => update('password', e.target.value)}
                            onFocus={() => setFocus('password')}
                            onBlur={() => setFocus('')}
                            placeholder="Min 8 characters"
                            required
                        />

                        {error && <div style={s.err}>{error}</div>}

                        <button
                            type="submit"
                            className="og-btn-primary"
                            style={{ ...s.btn, opacity: loading ? 0.7 : 1 }}
                            disabled={loading}
                        >
                            {loading ? 'Creating account...' : 'Create Account →'}
                        </button>
                    </form>

                    <div style={s.divider}>
                        <div style={s.divLine} />
                        <span>already have an account?</span>
                        <div style={s.divLine} />
                    </div>

                    <div style={s.login}>
                        <span style={s.loginL} onClick={() => navigate('/login')}>Sign in →</span>
                    </div>

                    <div style={s.terms}>
                        By registering you agree to our{' '}
                        <span style={s.termsL} onClick={() => navigate('/terms')}>Terms of Service</span>
                        {' '}and{' '}
                        <span style={s.termsL} onClick={() => navigate('/terms')}>Privacy Policy</span>.
                    </div>
                </div>
            </div>
        </div>
    );
}
